import React from 'react';
import { ModalTransition } from '@atlaskit/modal-dialog';

import Inputs from 'components/Inputs';
import Row from 'components/Common/Row';
import FleetOpsStaffOnly from 'components/Common/FleetOpsStaffOnly';
import Button from 'kingpin/atoms/Button';
import FormInput from 'kingpin/forms/FormInput';
import Icon from 'kingpin/atoms/Icon';
import Alert from 'kingpin/Alert';
import Tooltip from 'components/Tooltip';
import usePopup from 'hooks/usePopup';
import BonusOverridesModal from 'screens/Settings/Advanced/BonusOverrides/BonusOverridesModal';
import BoardSettingsHeader from '../BoardSettingsHeader/BoardSettingsHeader';

const BoardOverridesSettings = ({
  isSlideoutOverrideButtonEnabled,
  setIsSlideoutOverrideButtonEnabled,
  isSlideoutOverrideEnabled,
  setIsSlideoutOverrideEnabled,
  isSubPeriodBonusBoard,
  isWeeklyFactTableOverrideEnabled,
  setIsWeeklyFactTableOverrideEnabled,
  isDisqualificationReasonsEnabled,
  setIsDisqualificationReasonsEnabled,
  bonusProgram,
  hasUnsavedChanges,
  handleSave,
}: {
  isSlideoutOverrideButtonEnabled: boolean;
  setIsSlideoutOverrideButtonEnabled: (newValue: boolean) => void;
  isSlideoutOverrideEnabled: boolean;
  setIsSlideoutOverrideEnabled: (newValue: boolean) => void;
  isSubPeriodBonusBoard: boolean;
  isWeeklyFactTableOverrideEnabled: boolean;
  setIsWeeklyFactTableOverrideEnabled: (newValue: boolean) => void;
  isDisqualificationReasonsEnabled: boolean;
  setIsDisqualificationReasonsEnabled: (newValue: boolean) => void;
  // The board's bonus programme, when it has one — enables the account-wide
  // reward-visibility shortcut.
  bonusProgram: BonusTypes.BonusProgram | undefined;
  hasUnsavedChanges: boolean;
  handleSave: () => void;
}) => {
  const {
    isOpen: isRewardVisibilityOpen,
    open: openRewardVisibility,
    close: closeRewardVisibility,
  } = usePopup();

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <BoardSettingsHeader
        title="Overrides"
        description="Controls how bonus overrides behave in this board's slideout."
      />

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          overflowY: 'scroll',
          paddingRight: 8,
          paddingLeft: 8,
          borderBottom: `1px solid var(--color-grid-border)`,
        }}
      >
        <FormInput label={'Override button in slideout'}>
          <Row centerAlign style={{ gap: 8 }}>
            <Inputs.Toggle
              testId={'slideout-override-button-toggle'}
              value={isSlideoutOverrideButtonEnabled}
              onChange={setIsSlideoutOverrideButtonEnabled}
            />
            <div style={{ cursor: 'help' }}>
              <Tooltip
                content={
                  "When on, the slideout shows an Override button for bonus drivers. When off, the button is hidden and the other override settings below don't apply."
                }
              >
                <Icon icon={'help'} />
              </Tooltip>
            </div>
          </Row>
        </FormInput>

        <FormInput label={'Overrides happen within the slideout'}>
          <Row centerAlign style={{ gap: 8 }}>
            <Inputs.Toggle
              testId={'slideout-override-toggle'}
              value={isSlideoutOverrideEnabled}
              onChange={setIsSlideoutOverrideEnabled}
              isDisabled={!isSlideoutOverrideButtonEnabled}
            />
            <div style={{ cursor: 'help' }}>
              <Tooltip
                content={
                  'When on, clicking Override transforms the slideout into the bonus override workflow in place. When off, it opens the entity app popup to override there instead.'
                }
              >
                <Icon icon={'help'} />
              </Tooltip>
            </div>
          </Row>
        </FormInput>

        <FormInput label={'Show disqualification reasons'}>
          <Row centerAlign style={{ gap: 8 }}>
            <Inputs.Toggle
              testId={'disqualification-reasons-toggle'}
              value={isDisqualificationReasonsEnabled}
              onChange={setIsDisqualificationReasonsEnabled}
              isDisabled={
                !isSlideoutOverrideButtonEnabled || !isSlideoutOverrideEnabled
              }
            />
            <div style={{ cursor: 'help' }}>
              <Tooltip
                content={
                  'When on, the eligibility banner in the slideout override screen lists the qualifiers and disqualifiers the driver is failing, and flags any that are overridden. Requires overrides to happen within the slideout.'
                }
              >
                <Icon icon={'help'} />
              </Tooltip>
            </div>
          </Row>
        </FormInput>

        {isSubPeriodBonusBoard && (
          <FormInput label={'Weekly fact-table overrides'}>
            <Row centerAlign style={{ gap: 8 }}>
              <Inputs.Toggle
                testId={'weekly-fact-table-override-toggle'}
                value={isWeeklyFactTableOverrideEnabled}
                onChange={setIsWeeklyFactTableOverrideEnabled}
                isDisabled={!isSlideoutOverrideButtonEnabled}
              />
              <div style={{ cursor: 'help' }}>
                <Tooltip
                  content={
                    'When on, override mode in the slideout is driven by the per-week fact table: each configured pay column becomes a per-week payout dropdown, and the eligibility column (qualifiesForBonus) becomes a per-week Qualified/Disqualified override — each change is saved as one override per affected week. The fact-table sections on this board must be configured with these columns for the overrides to appear.'
                  }
                >
                  <Icon icon={'help'} />
                </Tooltip>
              </div>
            </Row>
          </FormInput>
        )}

        {bonusProgram && (
          <FleetOpsStaffOnly>
            <FormInput label={'Rewards shown while overriding'}>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: 8,
                }}
              >
                <Alert
                  type="Info"
                  body={`Account-wide setting — changes apply to every board and app that uses ${bonusProgram.name}, not just this board.`}
                />
                <Button
                  type="Secondary"
                  size="Small"
                  label="Manage reward visibility"
                  onClick={openRewardVisibility}
                  testId={'manage-reward-visibility-button'}
                />
              </div>
            </FormInput>
            <ModalTransition>
              {isRewardVisibilityOpen && (
                <BonusOverridesModal
                  bonusProgram={bonusProgram}
                  close={closeRewardVisibility}
                />
              )}
            </ModalTransition>
          </FleetOpsStaffOnly>
        )}
      </div>

      <Row
        spaceBetween
        centerAlign
        style={{ padding: '8px 0', height: '46px' }}
      >
        <div />
        <Button
          type="Primary"
          size="Small"
          isDisabled={!hasUnsavedChanges}
          onClick={handleSave}
          label="Save"
        />
      </Row>
    </div>
  );
};

export default BoardOverridesSettings;
